// DropPin PWA bootstrap — v0.17.2.
//
// Registers the service worker (production only — registering it in
// dev would cache Vite HMR-served bundles and break next reload).
//
// Also wires the beforeinstallprompt event into a window-level
// promise we can resolve later from a button click. Browsers fire
// this event ONCE per page load, before the user has clicked
// anything. We stash it for the "Install app" UI to use.

import { APP_VERSION } from './utils/version';

export function registerSW() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Native shell (Capacitor): NEVER register the SW. The wrapper bundles
  // its own copy of dist and updates via store release — a cache-first SW
  // in the WebView would fight the bundled assets and serve a stale shell
  // forever. window.Capacitor is injected by the native runtime; the
  // protocol check is a belt-and-braces fallback.
  const cap = window.Capacitor;
  if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) return;
  if (window.location.protocol === 'capacitor:') return;

  // Skip in dev — Vite serves modules ad-hoc and the SW would cache
  // outdated transforms.
  if (import.meta.env.DEV) return;

  // The worker in charge when this page was opened (null on a first visit).
  let current = navigator.serviceWorker.controller;
  let reloading = false;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => {
        // v0.239.0 — LOOK FOR A NEW BUILD, AND TAKE IT. Brett, on the desktop
        // app after two releases: "it still hasnt changed." The worker was
        // registered once and never asked to update, so an installed window
        // that stays open ran the build it opened with for as long as it
        // lived. Now: check on load, when the window comes back to the
        // foreground, and every ten minutes; a new worker is told to take
        // over at once (sw.js: SKIP_WAITING) and the page reloads once when it
        // has — once, guarded, so two workers can never ping-pong a reload.
        const check = () => reg.update().catch(() => {});
        document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
        setInterval(check, 10 * 60 * 1000);
        reg.addEventListener('updatefound', () => {
          const w = reg.installing;
          if (!w) return;
          w.addEventListener('statechange', () => { if (w.state === 'installed' && navigator.serviceWorker.controller) w.postMessage({ type: 'SKIP_WAITING' }); });
        });
      })
      .catch((e) => console.warn('[PWA] SW registration failed:', e));
    // v0.286.0 — RELOAD ONLY A PAGE THAT IS RUNNING AN OLDER BUILD. Brett: "when
    // the client droppin report first opens it splashes the report then it
    // disapears." On a first visit the new worker takes charge of the page
    // (clients.claim), which fires controllerchange too, and v0.239 reloaded on
    // it: the report showed, then the page started again. Pages come from the
    // network first, so a page the worker has just taken over is already the
    // newest build. Now: no worker before = nothing to swap; a worker of this
    // page's own build = nothing to swap; anything else (a window left open
    // across a release) reloads, as v0.239 meant. On a client's report the
    // reload waits while they have words typed (reloadWhenFree, below).
    navigator.serviceWorker.addEventListener('controllerchange', async () => {
      const prev = current;
      const now = navigator.serviceWorker.controller;
      current = now;
      if (reloading || !prev || !now) return;
      if (buildOf(await workerVersion(now)) === APP_VERSION) return;
      reloading = true;
      reloadWhenFree();
    });
  });

  // beforeinstallprompt fires when the browser thinks the user could
  // install the app. We stash the event so an "Install" button can
  // resolve it later. Chrome / Edge / Samsung fire it; Safari does not
  // (Safari uses Share → Add to Home Screen instead, no event).
  window._droppinInstallPrompt = null;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    window._droppinInstallPrompt = e;
    window.dispatchEvent(new CustomEvent('droppin:installavailable'));
  });

  // When the user finishes installing (or dismisses), clean up.
  window.addEventListener('appinstalled', () => {
    window._droppinInstallPrompt = null;
    window.dispatchEvent(new CustomEvent('droppin:installed'));
  });
}

// The worker's CACHE_VERSION ('droppin-v0.286.0-…'), or null if it does not
// answer in 2 s — and null means "cannot tell", which reloads.
function workerVersion(worker) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), 2000);
    try {
      const ch = new MessageChannel();
      ch.port1.onmessage = (e) => { clearTimeout(timer); resolve(e.data?.version || null); };
      worker.postMessage({ type: 'GET_VERSION' }, [ch.port2]);
    } catch (_) { clearTimeout(timer); resolve(null); }
  });
}

function buildOf(cacheVersion) {
  const m = /^droppin-v(\d+\.\d+\.\d+)/.exec(String(cacheVersion || ''));
  return m ? m[1] : null;
}

// A client part-way through a reply on their report link keeps it: the reload
// waits until no box holds words and they are not typing in a field.
function reloadWhenFree() {
  const onReport = /^\/r\//.test(window.location.pathname);
  const typing = onReport && (
    Array.from(document.querySelectorAll('textarea')).some((t) => t.value.trim())
    || (/^(INPUT|TEXTAREA)$/.test(document.activeElement?.tagName || '') && document.activeElement.value));
  if (typing) { setTimeout(reloadWhenFree, 3000); return; }
  window.location.reload();
}

// Helper for the Install button — fires the stashed prompt if there
// is one. Returns a promise that resolves to 'accepted' | 'dismissed'
// | 'unavailable'.
export async function showInstallPrompt() {
  const p = (typeof window !== 'undefined') ? window._droppinInstallPrompt : null;
  if (!p) return 'unavailable';
  p.prompt();
  const choice = await p.userChoice;
  window._droppinInstallPrompt = null;
  return choice?.outcome || 'dismissed';
}
